<template>
  <div class="pf-page">
    <MainNavbar />

    <!-- Title -->
    <header class="pf-title-band">
      <p class="pf-kicker">Youth talent development</p>
      <h1 class="pf-title">Pathfinder</h1>
    </header>

    <!-- Hero visual (16:9, full viewport width) -->
    <figure class="pf-visual pf-visual--hero">
      <img
        :src="heroImage"
        alt="Pathfinder youth talent development"
        class="pf-visual__img"
        width="1920"
        height="1080"
        decoding="async"
        fetchpriority="high"
      />
    </figure>

    <section class="pf-subtitle-band" aria-label="Tagline">
      <p class="pf-subtitle">The New Dawn of Nurturing African Talents</p>
      <p class="pf-subtitle-note">
        A structured pathway from early spotting at home to elite readiness—support, data, and
        real opportunities for young athletes across Kenya and Africa.
      </p>
    </section>

    <!-- Concept diagram (16:9, full width) -->
    <figure class="pf-visual pf-visual--concept">
      <img
        :src="conceptImage"
        alt="The Pathfinder concept ecosystem"
        class="pf-visual__img"
        width="1920"
        height="1080"
        loading="lazy"
        decoding="async"
      />
    </figure>

    <!-- Concept copy -->
    <section class="pf-block" aria-labelledby="pf-concept-heading">
      <div class="pf-block__inner">
        <p class="pf-block__kicker">The concept</p>
        <h2 id="pf-concept-heading" class="pf-block__title">One ecosystem, every stage</h2>
        <p class="pf-block__lead">
          Pathfinder nurtures a child’s growth in a chosen sport—from parents and mentors to
          coaching, equipment, daily practice, performance tracking, and agency support.
        </p>
        <p class="pf-block__body">
          Young African talent gets a clear route to bigger challenges—with structure, measurable
          progress, and confidence instead of guesswork.
        </p>
      </div>
    </section>

    <!-- Eight pillars -->
    <section class="pf-features" aria-labelledby="pf-pillars-heading">
      <div class="pf-features__head">
        <p class="pf-block__kicker">Pillars</p>
        <h2 id="pf-pillars-heading" class="pf-block__title">What powers the journey</h2>
        <p class="pf-block__desc">Eight connected supports from first steps to peak performance.</p>
      </div>
      <div class="pf-features__grid">
        <PathfinderPillarCard
          v-for="pillar in pillars"
          :key="pillar.id"
          :pillar="pillar"
        />
      </div>
    </section>

    <!-- Programme modules -->
    <section class="pf-block pf-block--alt" aria-labelledby="pf-programme-heading">
      <div class="pf-block__inner">
        <p class="pf-block__kicker">Programme</p>
        <h2 id="pf-programme-heading" class="pf-block__title">Five programme modules</h2>
        <p class="pf-block__desc">
          Connected tools—not isolated features—that teams, parents, and mentors use together.
        </p>
        <div class="pf-modules-grid">
          <PathfinderProgrammeCard
            v-for="module in programme"
            :key="module.id"
            :module="module"
          />
        </div>
      </div>
    </section>

    <!-- Age tiers -->
    <section class="pf-block" aria-labelledby="pf-tiers-heading">
      <div class="pf-block__inner">
        <p class="pf-block__kicker">Development tiers</p>
        <h2 id="pf-tiers-heading" class="pf-block__title">Growth by age</h2>
        <p class="pf-block__desc">
          Tracking measures trajectory—not one tournament—across four clear stages.
        </p>
        <ol class="pf-tiers">
          <li v-for="tier in ageTiers" :key="tier.tier" class="pf-tier">
            <span class="pf-tier__num">{{ tier.tier }}</span>
            <div class="pf-tier__body">
              <div class="pf-tier__meta">
                <span class="pf-tier__ages">Ages {{ tier.ages }}</span>
                <strong class="pf-tier__title">{{ tier.title }}</strong>
              </div>
              <p class="pf-tier__question">{{ tier.question }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- Sports -->
    <section class="pf-block pf-block--alt" aria-labelledby="pf-sports-heading">
      <div class="pf-block__inner pf-block__inner--center">
        <p class="pf-block__kicker">Sports</p>
        <h2 id="pf-sports-heading" class="pf-block__title">Plans per child, per sport</h2>
        <p class="pf-block__desc">One subscription covers one child in one of these disciplines.</p>
        <ul class="pf-sport-chips">
          <li v-for="sport in sports" :key="sport.slug">
            <span class="pf-sport-chip">
              <i :class="sport.icon" aria-hidden="true" />
              {{ sport.name }}
            </span>
          </li>
        </ul>
      </div>
    </section>

    <!-- Plans -->
    <section class="pf-block" aria-labelledby="pf-plans-heading">
      <div class="pf-block__inner">
        <p class="pf-block__kicker">Pricing</p>
        <h2 id="pf-plans-heading" class="pf-block__title">Choose a plan</h2>
        <p class="pf-block__desc">
          Per child, per month. Start with Basic or unlock the full Pathfinder ecosystem.
        </p>
        <div class="pf-plans-grid">
          <PathfinderPlanCard
            v-for="plan in plans"
            :key="plan.id"
            :plan="plan"
            :programme-modules="programme"
            @select="onSelectPlan"
          />
        </div>
        <p class="pf-footnote">
          <i class="fa-solid fa-circle-info" aria-hidden="true" />
          Checkout and child onboarding coming soon—plan buttons are preview only for now.
        </p>
      </div>
    </section>

    <!-- Launch countdown -->
    <section class="pf-launch" aria-labelledby="pf-launch-heading">
      <div class="pf-launch__glow" aria-hidden="true" />
      <div class="pf-launch__inner">
        <p class="pf-launch__kicker">Mark your calendar</p>
        <h2 id="pf-launch-heading" class="pf-launch__title">The new dawn is almost here</h2>
        <p class="pf-launch__tagline">
          Pathfinder officially launches on
          <time datetime="2027-01-04">4 January 2027</time>.
          A continent of young talent is about to get structure, support, and a real path forward.
        </p>

        <div v-if="launchHasPassed" class="pf-launch__live">
          <i class="fa-solid fa-rocket" aria-hidden="true" />
          <p>Pathfinder is live. The journey starts now.</p>
        </div>

        <div
          v-else
          class="pf-countdown"
          role="timer"
          :aria-label="countdownAriaLabel"
        >
          <div
            v-for="unit in countdownUnits"
            :key="unit.label"
            class="pf-countdown__unit"
            :class="{ 'pf-countdown__unit--seconds': unit.label === 'Seconds' }"
          >
            <span class="pf-countdown__value" aria-hidden="true">{{ pad(unit.value) }}</span>
            <span class="pf-countdown__label">{{ unit.label }}</span>
          </div>
        </div>

        <p v-if="!launchHasPassed" class="pf-launch__hint">
          <i class="fa-solid fa-hourglass-half" aria-hidden="true" />
          Be among the first families to shape Africa's sporting future.
        </p>
      </div>
    </section>

    <!-- Closing CTA -->
    <section class="pf-cta" aria-label="Summary">
      <h2 class="pf-cta__title">Support. Structure. Opportunity.</h2>
      <p class="pf-cta__body">
        Pathfinder empowers young athletes to reach their full potential and step up to Africa’s
        biggest stages—with clarity at every step.
      </p>
    </section>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import PathfinderPillarCard from "@/components/pathfinder/PathfinderPillarCard.vue";
import PathfinderProgrammeCard from "@/components/pathfinder/PathfinderProgrammeCard.vue";
import PathfinderPlanCard from "@/components/pathfinder/PathfinderPlanCard.vue";
import heroImage from "@/assets/images/pathfinder/pathfinderimage.png";
import conceptImage from "@/assets/images/pathfinder/concept.png";
import {
  PATHFINDER_PILLARS,
  PATHFINDER_PROGRAMME,
  PATHFINDER_AGE_TIERS,
  PATHFINDER_SPORTS,
  PATHFINDER_PLANS,
} from "@/routes/pathfinder/constants";

const PATHFINDER_LAUNCH_MS = new Date("2027-01-04T00:00:00+03:00").getTime();

export default {
  name: "PathfinderIntro",
  components: {
    MainNavbar,
    MainFooter,
    PathfinderPillarCard,
    PathfinderProgrammeCard,
    PathfinderPlanCard,
  },
  data() {
    return {
      heroImage,
      conceptImage,
      pillars: PATHFINDER_PILLARS,
      programme: PATHFINDER_PROGRAMME,
      ageTiers: PATHFINDER_AGE_TIERS,
      sports: PATHFINDER_SPORTS,
      plans: PATHFINDER_PLANS,
      countdown: { days: 0, hours: 0, minutes: 0, seconds: 0 },
      launchHasPassed: false,
      countdownTimer: null,
    };
  },
  computed: {
    countdownUnits() {
      return [
        { value: this.countdown.days, label: "Days" },
        { value: this.countdown.hours, label: "Hours" },
        { value: this.countdown.minutes, label: "Minutes" },
        { value: this.countdown.seconds, label: "Seconds" },
      ];
    },
    countdownAriaLabel() {
      const { days, hours, minutes, seconds } = this.countdown;
      return `${days} days, ${hours} hours, ${minutes} minutes, and ${seconds} seconds until Pathfinder launches on 4 January 2027`;
    },
  },
  mounted() {
    this.updateCountdown();
    this.countdownTimer = setInterval(this.updateCountdown, 1000);
  },
  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  methods: {
    updateCountdown() {
      const diff = PATHFINDER_LAUNCH_MS - Date.now();
      if (diff <= 0) {
        this.launchHasPassed = true;
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      this.countdown = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };
    },
    pad(value) {
      return String(value).padStart(2, "0");
    },
    onSelectPlan(planId) {
      const plan = this.plans.find((p) => p.id === planId);
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: {
            title: "Coming soon",
            message: `${plan?.name || "Plan"} subscriptions will open shortly. Thank you for your interest in Pathfinder.`,
            type: "info",
          },
        }),
      );
    },
  },
};
</script>

<style scoped src="./pathfinderIntro.css"></style>
